import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../components/AppIcon';
import { auditoriaFinancieraService } from '../../services/financialService';

const ACCENT = '#D6EF4E';

const AUDIT_TABLAS = ['Todos', 'fin_gastos', 'fin_gastos_recurrentes', 'fin_facturas', 'fin_movimientos_tesoreria', 'fin_ocr_documentos', 'fin_centros_coste', 'fin_cuentas'];
const AUDIT_ACCIONES = ['Todos', 'CREACION', 'EDICION', 'ELIMINACION', 'CONCILIACION', 'CAMBIO_ESTADO', 'CONFIRMACION_OCR'];

const ACCION_COLORS = {
  CREACION: '#34D399', EDICION: '#60A5FA', ELIMINACION: '#F87171',
  CONCILIACION: '#A78BFA', CAMBIO_ESTADO: '#FBBF24', CONFIRMACION_OCR: '#D6EF4E',
};

const ACCION_ICONS = {
  CREACION: 'Plus', EDICION: 'Edit', ELIMINACION: 'Trash2',
  CONCILIACION: 'GitMerge', CAMBIO_ESTADO: 'RefreshCw', CONFIRMACION_OCR: 'ScanLine',
};

const fmt = (n) => (typeof n === 'number' ? n : parseFloat(n) || 0);

export default function AuditoriaTotal() {
  const [tablaFilter, setTablaFilter] = useState('Todos');
  const [accionFilter, setAccionFilter] = useState('Todos');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [registros, setRegistros] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [regs, kpisData] = await Promise.all([
        auditoriaFinancieraService?.getRecent({
          limit: 200,
          tablaAfectada: tablaFilter !== 'Todos' ? tablaFilter : undefined,
          accion: accionFilter !== 'Todos' ? accionFilter : undefined,
          search: search || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        }),
        auditoriaFinancieraService?.getKPIs(),
      ]);
      setRegistros(regs);
      setKpis(kpisData);
    } catch (err) {
      setError(err?.message || 'Error al cargar auditoría');
    } finally {
      setLoading(false);
    }
  }, [tablaFilter, accionFilter, search, dateFrom, dateTo]);

  useEffect(() => { loadData(); }, [loadData]);

  const kpiCards = [
    { label: 'Registros Hoy', value: kpis?.registrosHoy || 0, icon: 'Activity', color: ACCENT },
    { label: 'Creaciones', value: kpis?.creaciones || 0, icon: 'Plus', color: '#34D399' },
    { label: 'Ediciones', value: kpis?.ediciones || 0, icon: 'Edit', color: '#60A5FA' },
    { label: 'Confirmaciones OCR', value: kpis?.confirmacionesOCR || 0, icon: 'ScanLine', color: '#A78BFA' },
    { label: 'Eliminaciones', value: kpis?.eliminaciones || 0, icon: 'Trash2', color: '#F87171' },
    { label: 'Usuarios Activos', value: kpis?.usuariosActivos || 0, icon: 'Users', color: '#FBBF24' },
  ];

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${String(d?.getDate())?.padStart(2, '0')}/${String(d?.getMonth() + 1)?.padStart(2, '0')}/${d?.getFullYear()}`;
  };

  const formatTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${String(d?.getHours())?.padStart(2, '0')}:${String(d?.getMinutes())?.padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-5 font-oranienbaum text-white/30" style={{ fontSize: 'var(--fs-sm)' }}>
        <Icon name="Home" size={13} />
        <span>/</span>
        <span className="text-[#D6EF4E]/60">ERP</span>
        <span>/</span>
        <span className="text-white/50">Auditoría Total</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(214,239,78,0.10)', border: '1px solid rgba(214,239,78,0.25)' }}>
            <Icon name="Shield" size={22} className="text-[#D6EF4E]" />
          </div>
          <div>
            <h1 className="font-oranienbaum font-bold text-white tracking-wide" style={{ fontSize: 'var(--fs-page-title)' }}>Auditoría Total</h1>
            <p className="font-oranienbaum text-white/40" style={{ fontSize: 'var(--fs-sm)' }}>Registro real de todas las operaciones financieras del sistema</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadData} className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-oranienbaum border transition-all"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.6)', fontSize: 'var(--fs-btn)' }}>
            <Icon name="RefreshCw" size={15} /> Actualizar
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
          <Icon name="AlertCircle" size={15} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-xs">{error}</p>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        {kpiCards?.map((kpi, i) => (
          <div key={i} className="rounded-2xl p-4 flex flex-col gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${kpi?.color}15`, border: `1px solid ${kpi?.color}30` }}>
              <Icon name={kpi?.icon} size={15} style={{ color: kpi?.color }} />
            </div>
            <p className="font-oranienbaum font-bold text-white" style={{ fontSize: 'var(--fs-kpi-value)' }}>
              {loading ? '...' : kpi?.value}
            </p>
            <p className="font-oranienbaum text-white/45 mt-0.5" style={{ fontSize: 'var(--fs-kpi-label)' }}>{kpi?.label}</p>
          </div>
        ))}
      </div>

      {/* Acción filter pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        {AUDIT_ACCIONES?.map(acc => (
          <button key={acc} onClick={() => setAccionFilter(acc)}
            className="px-3 py-1.5 rounded-xl font-oranienbaum transition-all"
            style={{
              background: accionFilter === acc ? `${ACCION_COLORS?.[acc] || ACCENT}15` : 'rgba(255,255,255,0.04)',
              border: `1px solid ${accionFilter === acc ? `${ACCION_COLORS?.[acc] || ACCENT}35` : 'rgba(255,255,255,0.08)'}`,
              color: accionFilter === acc ? (ACCION_COLORS?.[acc] || ACCENT) : 'rgba(255,255,255,0.55)',
              fontSize: 'var(--fs-sm)',
              fontWeight: accionFilter === acc ? 600 : 400,
            }}>
            {acc}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] rounded-xl px-3 border"
          style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.10)', minHeight: 'var(--control-height-sm)' }}>
          <Icon name="Search" size={14} className="text-white/35" />
          <input value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Buscar descripción, usuario, tabla..."
            className="flex-1 bg-transparent outline-none font-oranienbaum text-white placeholder-white/25"
            style={{ fontSize: 'var(--fs-base)' }} />
        </div>
        <select value={tablaFilter} onChange={e => setTablaFilter(e?.target?.value)}
          className="font-oranienbaum rounded-xl px-3 border outline-none text-white"
          style={{ background: '#0D0D0D', borderColor: 'rgba(255,255,255,0.10)', minHeight: 'var(--control-height-sm)', fontSize: 'var(--fs-base)' }}>
          {AUDIT_TABLAS?.map(t => <option key={t} value={t} style={{ background: '#0D0D0D' }}>{t}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e?.target?.value)}
          className="font-oranienbaum rounded-xl px-3 border outline-none text-white"
          style={{ background: '#0D0D0D', borderColor: 'rgba(255,255,255,0.10)', minHeight: 'var(--control-height-sm)', fontSize: 'var(--fs-base)' }} />
        <input type="date" value={dateTo} onChange={e => setDateTo(e?.target?.value)}
          className="font-oranienbaum rounded-xl px-3 border outline-none text-white"
          style={{ background: '#0D0D0D', borderColor: 'rgba(255,255,255,0.10)', minHeight: 'var(--control-height-sm)', fontSize: 'var(--fs-base)' }} />
        {(dateFrom || dateTo || search || tablaFilter !== 'Todos' || accionFilter !== 'Todos') && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); setSearch(''); setTablaFilter('Todos'); setAccionFilter('Todos'); }}
            className="px-3 py-2 rounded-xl font-oranienbaum text-white/40 hover:text-white/70 transition-colors text-xs"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            Limpiar
          </button>
        )}
      </div>

      {/* Audit table */}
      <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="overflow-x-auto">
          <table className="w-full font-oranienbaum">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.03)' }}>
                {['Acción', 'Tabla', 'Usuario', 'Fecha', 'Hora', 'Descripción', '']?.map(h => (
                  <th key={h} className="text-left px-4 py-3 text-white/40 font-medium whitespace-nowrap" style={{ fontSize: 'var(--fs-table-header)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 rounded-full border-2 border-white/10 border-t-[#D6EF4E] animate-spin" />
                    <span className="font-oranienbaum text-white/30 text-xs">Cargando auditoría...</span>
                  </div>
                </td></tr>
              ) : registros?.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <Icon name="Shield" size={28} className="text-white/20" />
                    <p className="font-oranienbaum text-white/40" style={{ fontSize: 'var(--fs-base)' }}>
                      Sin registros de auditoría financiera
                    </p>
                    <p className="font-oranienbaum text-white/25 text-xs">
                      Los registros aparecerán aquí cuando se realicen operaciones financieras
                    </p>
                  </div>
                </td></tr>
              ) : registros?.map(entry => (
                <tr key={entry?.id} className="hover:bg-white/3 transition-colors cursor-pointer"
                  onClick={() => setSelectedEntry(entry)}
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Icon name={ACCION_ICONS?.[entry?.accion] || 'Circle'} size={13}
                        style={{ color: ACCION_COLORS?.[entry?.accion] || '#fff' }} />
                      <span className="px-2 py-0.5 rounded-full font-medium"
                        style={{ background: `${ACCION_COLORS?.[entry?.accion] || '#fff'}15`, color: ACCION_COLORS?.[entry?.accion] || '#fff', fontSize: 'var(--fs-badge)' }}>
                        {entry?.accion}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white/50" style={{ fontSize: 'var(--fs-table-row)' }}>
                    {entry?.tabla_afectada?.replace('fin_', '')?.replace(/_/g, ' ')}
                  </td>
                  <td className="px-4 py-3 text-white/60" style={{ fontSize: 'var(--fs-table-row)' }}>
                    {entry?.usuario_email || '—'}
                  </td>
                  <td className="px-4 py-3 text-white/60" style={{ fontSize: 'var(--fs-table-row)' }}>
                    {formatDate(entry?.created_at)}
                  </td>
                  <td className="px-4 py-3 text-white/50" style={{ fontSize: 'var(--fs-table-row)' }}>
                    {formatTime(entry?.created_at)}
                  </td>
                  <td className="px-4 py-3 text-white/70 max-w-[280px] truncate" style={{ fontSize: 'var(--fs-table-row)' }}>
                    {entry?.descripcion || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <button className="w-8 h-8 rounded-lg flex items-center justify-center text-white/35 hover:text-white hover:bg-white/8 transition-all">
                      <Icon name="Eye" size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {registros?.length > 0 && (
          <div className="px-4 py-3 border-t border-white/5 flex items-center justify-between">
            <span className="font-oranienbaum text-white/30 text-xs">{registros?.length} registros</span>
            <span className="font-oranienbaum text-white/20 text-xs">Datos reales · fin_auditoria</span>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selectedEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.80)' }}>
          <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: '#111', border: `1px solid ${ACCENT}25` }}>
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
              <div className="flex items-center gap-2">
                <Icon name="Shield" size={16} className="text-[#D6EF4E]" />
                <h3 className="font-oranienbaum font-bold text-white" style={{ fontSize: 'var(--fs-section-title)' }}>Detalle de Auditoría</h3>
              </div>
              <button onClick={() => setSelectedEntry(null)} className="text-white/40 hover:text-white transition-colors">
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Acción', value: selectedEntry?.accion },
                  { label: 'Tabla', value: selectedEntry?.tabla_afectada },
                  { label: 'Fecha', value: formatDate(selectedEntry?.created_at) },
                  { label: 'Hora', value: formatTime(selectedEntry?.created_at) },
                  { label: 'Usuario', value: selectedEntry?.usuario_email || '—' },
                  { label: 'Registro ID', value: selectedEntry?.registro_id ? selectedEntry?.registro_id?.substring(0, 8) + '...' : '—' },
                ]?.map((item, i) => (
                  <div key={i}>
                    <p className="font-oranienbaum text-white/40 text-xs mb-1">{item?.label}</p>
                    <p className="font-oranienbaum text-white font-medium" style={{ fontSize: 'var(--fs-sm)' }}>{item?.value}</p>
                  </div>
                ))}
              </div>
              {selectedEntry?.descripcion && (
                <div className="pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                  <p className="font-oranienbaum text-white/40 text-xs mb-1">Descripción</p>
                  <p className="font-oranienbaum text-white/70 text-sm">{selectedEntry?.descripcion}</p>
                </div>
              )}
              {(selectedEntry?.valores_anteriores || selectedEntry?.valores_nuevos) && (
                <div className="grid grid-cols-2 gap-4 pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                  {selectedEntry?.valores_anteriores && (
                    <div className="rounded-xl p-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
                      <p className="font-oranienbaum text-red-400/70 text-xs mb-1">Valores Anteriores</p>
                      <pre className="font-oranienbaum text-red-400 text-xs overflow-auto max-h-24">
                        {JSON.stringify(selectedEntry?.valores_anteriores, null, 2)}
                      </pre>
                    </div>
                  )}
                  {selectedEntry?.valores_nuevos && (
                    <div className="rounded-xl p-3" style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.20)' }}>
                      <p className="font-oranienbaum text-green-400/70 text-xs mb-1">Valores Nuevos</p>
                      <pre className="font-oranienbaum text-green-400 text-xs overflow-auto max-h-24">
                        {JSON.stringify(selectedEntry?.valores_nuevos, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
